import { useEffect, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { Button, Stack,Text } from '@ekhodealer/ekho-common/rev';
import { Popup } from '@ekhodealer/ekho-common/rev';

interface ArchiveInventoryModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	/** Total number of selected items to delete */
	totalSelected: number;
	onConfirm: () => void;
	onCancel: () => void;
	/** Whether the archive operation is currently pending */
	isArchiving?: boolean;
}

/**
 * Destructive confirmation modal for deleting inventory items
 * Shows simple confirmation with "Delete [N] inventory items?" title
 * and "This action cannot be undone" warning
 */
export const ArchiveInventoryModal = ({
	isOpen,
	onOpenChange,
	totalSelected,
	onConfirm,
	onCancel,
	isArchiving = false,
}: ArchiveInventoryModalProps) => {
	const prevIsArchivingRef = useRef(isArchiving);

	// Close modal when archiving operation completes
	useEffect(() => {
		// If we were archiving and now we're not, close the modal
		if (prevIsArchivingRef.current && !isArchiving) {
			onOpenChange(false);
		}
		prevIsArchivingRef.current = isArchiving;
	}, [isArchiving, onOpenChange]);

	const handleCancel = () => {
		onCancel();
		onOpenChange(false);
	};

	const handleConfirm = () => {
		onConfirm();
		// Don't close immediately - let the loading state show and close after operation completes
	};

	const title = `Archive ${totalSelected} inventory item${totalSelected !== 1 ? 's' : ''}?`;

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<Popup title={title}>
				<Stack padding='0' itemsSpacing='24'>
					{/* Warning message */}
					<Text size='body' color='secondary'>
						This action cannot be undone
					</Text>

					{/* Action buttons */}
					<>
						<style>
							{`
							.archive-modal-destructive-button button[data-rev-variant="filled"] {
								background-color: #ef4444 !important;
								color: white !important;
							}
							.archive-modal-destructive-button button[data-rev-variant="filled"]:hover:not(:disabled) {
								background-color: #dc2626 !important;
							}
							`}
						</style>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								paddingTop: '8px',
							}}>
							<Button
								variant='secondary'
								size='medium'
								onPress={handleCancel}
								isDisabled={isArchiving}>
								Don't archive
							</Button>
							<div className='archive-modal-destructive-button'>
								<Button
									variant='filled'
									size='medium'
									onPress={handleConfirm}
									isLoading={isArchiving}
									isDisabled={isArchiving}>
									Archive
								</Button>
							</div>
						</div>
					</>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
